import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Form, Typography, message, Space, Tag } from 'antd';
import ResponsiveModal from 'components/shared-components/ResponsiveModal';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { buildScreenDynamicApiResponseExample } from 'utils/metaFlowComponents';

const { Text, Paragraph } = Typography;

const ScreenEndpointSettingsModal = ({
  open,
  screen,
  previousPayloadKeys = [],
  onClose,
  onSave,
}) => {
  const [tenantApiUri, setTenantApiUri] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && screen) {
      setTenantApiUri(screen.tenant_api_uri || '');
    }
    if (!open) {
      setCopied(false);
    }
  }, [open, screen]);

  const example = useMemo(
    () => (screen ? buildScreenDynamicApiResponseExample(screen) : null),
    [screen]
  );

  const exampleJson = example ? JSON.stringify(example, null, 2) : '';

  const placeholders = useMemo(
    () =>
      [...new Set((previousPayloadKeys || []).map((key) => String(key).trim()).filter(Boolean))],
    [previousPayloadKeys]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exampleJson);
      message.success('Copied to clipboard');
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 5000);
    } catch {
      message.error('Could not copy');
    }
  };

  const insertPlaceholder = (key) => {
    const token = `{${key}}`;
    setTenantApiUri((current) => {
      const base = (current || '').trim();
      if (!base) {
        return `https://your-domain.com/api/resource/${token}`;
      }
      if (base.includes(token)) {
        message.info(`${token} is already in the URL`);
        return base;
      }
      // Append as path segment if URL has no trailing placeholder pattern.
      if (base.endsWith('/')) {
        return `${base}${token}`;
      }
      return `${base}/${token}`;
    });
  };

  const handleCopyPlaceholder = async (key) => {
    const token = `{${key}}`;
    try {
      await navigator.clipboard.writeText(token);
      message.success(`Copied ${token}`);
    } catch {
      message.error('Could not copy');
    }
  };

  const handleSave = () => {
    const trimmed = tenantApiUri.trim();
    if (!trimmed) {
      message.error('Tenant API endpoint is required');
      return;
    }

    // Allow {placeholders} in the path — validate with dummy values.
    const forValidation = trimmed.replace(/\{[a-zA-Z0-9_]+\}/g, 'placeholder');
    try {
      const parsed = new URL(forValidation);
      if (parsed.protocol !== 'https:') {
        message.error('Endpoint must use HTTPS');
        return;
      }
    } catch {
      message.error('Enter a valid URL (you can use {item_id} placeholders in the path)');
      return;
    }

    onSave(trimmed);
    onClose();
  };

  if (!screen) {
    return null;
  }

  return (
    <ResponsiveModal
      title={`Screen endpoint: ${screen.title || screen.id}`}
      open={open}
      onClose={onClose}
      width={600}
      destroyOnClose
      bodyStyle={{ padding: '10px' }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        Meta calls Waba once per screen interaction. Your GET endpoint must return all dynamic
        fields for this screen in a single response.
      </Paragraph>

      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item
          label="Tenant API endpoint (GET)"
          extra="Use {field_name} in the path for values from the previous step (e.g. {item_id}). All values are also sent as query params."
        >
          <Input
            value={tenantApiUri}
            onChange={(e) => setTenantApiUri(e.target.value)}
            placeholder="https://your-domain.com/api/events/{item_id}/tickets"
          />
        </Form.Item>
      </Form>

      {placeholders.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            Available placeholders (from previous screens)
          </Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Click to insert into the URL, or copy and paste where your API needs the value.
          </Paragraph>
          <Space wrap>
            {placeholders.map((key) => (
              <Tag
                key={key}
                color="blue"
                style={{ cursor: 'pointer', userSelect: 'all' }}
                onClick={() => insertPlaceholder(key)}
                onDoubleClick={() => handleCopyPlaceholder(key)}
              >
                {`{${key}}`}
              </Tag>
            ))}
          </Space>
        </div>
      ) : (
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
          No payload keys from previous screens yet. On a later screen, keys like Navigation list
          &quot;Payload key on tap&quot; will appear here as {'{item_id}'}.
        </Paragraph>
      )}

      {example ? (
        <>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            Expected response from your endpoint
          </Text>
          <div style={{ position: 'relative' }}>
            <pre
              style={{
                margin: 0,
                padding: '12px 70px 12px 12px',
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.5,
                overflow: 'auto',
                maxHeight: 320,
                background: '#f5f5f5',
              }}
            >
              {exampleJson}
            </pre>
            <Button
              type={copied ? 'primary' : 'dashed'}
              size="small"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
              style={{ position: 'absolute', top: 8, right: 8 }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </>
      ) : null}
    </ResponsiveModal>
  );
};

export default ScreenEndpointSettingsModal;
